import { Redirect, Link } from "wouter";
import type { ReactNode } from "react";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  Compass,
  Database,
  ExternalLink,
  Gamepad2,
  HardDrive,
  Layers,
  Network,
  Radio,
  Rocket,
  ShieldCheck,
  Sliders,
  Smartphone,
  Volume2,
  Wrench,
} from "lucide-react";
import { useMe } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// ─────────────────────────────────────────────────────────────────────────
// /architecture — single-page orientation for human & agent devs.
// Platform-admin gated. This is the source of truth for how the system
// fits together; keep it in lockstep with the code. Style mirrors
// killerapps-dev/admin/architecture but wears cornholio's own theme.
//
// When you change a load-bearing fact (a route, a table, the pub/sub
// mechanism, the deploy target), update it HERE in the same commit.
// ─────────────────────────────────────────────────────────────────────────

export default function Architecture() {
  const { data: me, isLoading } = useMe();
  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (!me) return <Redirect to="/sign-in" />;
  if (!me.isPlatformAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <Wrench className="mx-auto text-muted-foreground" size={32} />
        <h1 className="text-2xl font-display uppercase tracking-tight">
          Restricted
        </h1>
        <p className="text-sm text-muted-foreground">
          The architecture overview is platform-admin only.
        </p>
        <Button asChild>
          <Link href="/dashboard">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <article className="px-4 sm:px-8 py-6 sm:py-10 max-w-5xl mx-auto space-y-10">
      <header className="space-y-2">
        <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
          /architecture
        </p>
        <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tight">
          Cornholio architecture
        </h1>
        <p className="text-muted-foreground max-w-3xl">
          A get-up-to-speed map of the whole system — built for human{" "}
          <em>and</em> agent developers. Read top-to-bottom for the mental
          model, or jump to a section. Diagrams show the shape; the amber
          callouts at the bottom flag the things that will bite you.
        </p>
      </header>

      <Orientation />

      {/* ── Tech stack ─────────────────────────────────────────────── */}
      <Section icon={Layers} title="Tech stack" id="stack">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {STACK.map((s) => (
            <Card key={s.k}>
              <CardContent className="p-3">
                <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
                  {s.k}
                </div>
                <div className="text-sm mt-0.5">{s.v}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* ── System overview ────────────────────────────────────────── */}
      <Section icon={Network} title="System overview" id="system">
        <Figure>
          <SystemDiagram />
        </Figure>
        <Prose>
          <strong>One Dockerized Express process</strong> serves the JSON API{" "}
          <em>and</em> the built React SPA (Vite emits into{" "}
          <Code>apps/api/public</Code>; Express static-serves it with an SPA
          fallback). It also runs the background stats worker in-process.
          Durable state is <strong>Neon Postgres</strong>; generated
          images/audio live in <strong>Cloudflare R2</strong>; AI calls out to{" "}
          <strong>Gemini</strong> (image gen + live-commentary text) and{" "}
          <strong>ElevenLabs</strong> (sound effects + commentary TTS voice). It
          is{" "}
          <strong>cross-machine safe</strong>: live game events fan out via
          Postgres <Code>LISTEN/NOTIFY</Code>, and the stats worker claims jobs
          with <Code>FOR UPDATE SKIP LOCKED</Code> — so a scale-out or rolling
          deploy never splits the realtime bus or double-computes a
          leaderboard. On Fly it currently runs auto-stop with{" "}
          <Code>min_machines_running = 0</Code> (cold-starts on first request).
        </Prose>
      </Section>

      {/* ── Monorepo & feature dirs ────────────────────────────────── */}
      <Section icon={Boxes} title="Monorepo & feature dirs" id="repo">
        <Prose>
          pnpm workspace. Two apps, four shared packages. Everything is
          organized by <strong>feature directory</strong> — a server feature is{" "}
          <Code>routes.ts</Code> + <Code>service.ts</Code> + <Code>types.ts</Code>{" "}
          (+ optional <Code>worker.ts</Code>); its tables live in the matching{" "}
          <Code>packages/db/src/features/&lt;name&gt;/schema.ts</Code>.
        </Prose>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Item label="apps/api" value="Express + Drizzle + node-postgres + zod + pino. Bundled with esbuild → dist/index.mjs. Serves /api/* and the SPA." />
          <Item label="apps/web" value="Vite + React 18 + wouter + TanStack Query + Tailwind v4 + Radix + Three.js. Emits to apps/api/public." />
          <Item label="packages/db" value="Drizzle schema (per-feature schema.ts + barrel), migrations/*.sql, migrate.ts runner." />
          <Item label="packages/game-core" value="Framework-free game rules: projectThrow + evaluateGameState. Imported by BOTH server and web." />
          <Item label="packages/api-spec" value="Shared API contract types / codegen source of truth." />
          <Item label="packages/shared" value="Cross-cutting helpers shared across apps." />
        </div>
      </Section>

      {/* ── Event-sourced game engine (the big one) ────────────────── */}
      <Section icon={Gamepad2} title="Event-sourced game engine (the big one)" id="engine">
        <Figure>
          <GameEngineDiagram />
        </Figure>
        <Prose>
          Every game is an <strong>append-only event log</strong> +{" "}
          <strong>materialized projection</strong>. A throw is a command:{" "}
          <Code>POST /api/games/:id/throws</Code> with an{" "}
          <Code>outcome</Code> (<Code>in_hole</Code> / <Code>on_board</Code> /{" "}
          <Code>miss</Code>), a resting <Code>landing</Code> {"{x,y,z}"}, and a
          client-generated <Code>clientEventUuid</Code>. The server appends a{" "}
          <Code>throw_landed</Code> row to <Code>game_events</Code>{" "}
          (monotonic gap-free <Code>event_seq</Code>), updates the{" "}
          <Code>games</Code> projection (<Code>throwsThisRound</Code>,{" "}
          <Code>bagsRemainingT1/2</Code>, scores, <Code>currentTurn</Code>,{" "}
          <Code>version</Code>), and may auto-append{" "}
          <Code>round_committed</Code> (on the 8th bag) and{" "}
          <Code>game_completed</Code>.
        </Prose>
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniCard title="One rule engine, two homes">
            The pure reducer <Code>projectThrow</Code> lives in{" "}
            <Code>@cornholio/game-core</Code>. The server wraps it in a DB
            transaction (<Code>games/play-state.ts</Code>); the client runs the{" "}
            <em>identical</em> function for optimistic solo play
            (<Code>gamePhase.ts</Code>). Same math both sides = no state drift.
          </MiniCard>
          <MiniCard title="Idempotent by construction">
            A retried throw carries the same <Code>clientEventUuid</Code>; a
            partial unique index on <Code>(game_id, client_event_uuid)</Code>{" "}
            dedupes at the DB. Flaky networks and offline replays can't
            double-count.
          </MiniCard>
          <MiniCard title="Solo vs remote throws">
            <strong>Solo / ghost:</strong> project locally → apply snapshot
            instantly → enqueue the POST in a durable localStorage queue
            (<Code>throwQueue.ts</Code>) that flushes in the background. Zero
            network in the critical path.{" "}
            <strong>Remote 1v1/2v2:</strong> await the POST, then apply the
            server's authoritative snapshot.
          </MiniCard>
          <MiniCard title="Versioned SSE, self-healing">
            <Code>GET /api/g/:code/play-state</Code> is the cold-start
            snapshot; <Code>/api/g/:code/stream</Code> pushes{" "}
            <Code>game_state</Code> events tagged with <Code>version</Code>.
            Clients keep the highest version and discard stragglers.
            Reconnects send <Code>Last-Event-ID</Code> so the server replays
            missed events; a 25s heartbeat keeps proxies open.
          </MiniCard>
        </div>
      </Section>

      {/* ── Virtual 3D renderer ────────────────────────────────────── */}
      <Section icon={Gamepad2} title="Virtual 3D renderer" id="renderer">
        <Prose>
          <Code>apps/web/src/lib/virtual3d.ts</Code> (~3k lines) is a bespoke
          Three.js renderer, lazy-loaded with the{" "}
          <Code>/dashboard/virtual</Code> route (~600KB chunk). It owns
          everything visual and physical about a throw; the{" "}
          <em>outcome</em> it produces is what the game engine records.
        </Prose>
        <ul className="text-sm space-y-2 list-disc pl-5 text-muted-foreground">
          <li>
            <strong className="text-foreground">Scene graph:</strong> two raked
            boards (far + near, mirrored 180°), procedural or image backdrop
            plane, per-team pile of remaining bags, and a camera-anchored{" "}
            <em>finger bag</em> for touch-throwing.
          </li>
          <li>
            <strong className="text-foreground">Player physics:</strong> a bag
            runs <Code>fly → slide → drop → rest</Code> — free-fall + gravity,
            friction-constrained slab sliding, in-hole drop, squash-on-impact
            deformation. <Code>outcomeAt(resting)</Code> is purely geometric.
          </li>
          <li>
            <strong className="text-foreground">Lip capture:</strong> a bag
            resting within <Code>holeLipCapture</Code> of the rim tips into the
            hole instead of perching — reclaims genuine near-miss 3s.
          </li>
          <li>
            <strong className="text-foreground">Scripted flights:</strong>{" "}
            opponent / ghost bags don't run physics — they arc to a{" "}
            <em>stored landing</em> on a deterministic parabola, then slide +
            (for 3s) drop into the throat. The originator records physics; every
            other device replays the same scripted arc.
          </li>
          <li>
            <strong className="text-foreground">Determinism:</strong>{" "}
            <Code>seededYaw(x,z)</Code> hashes the landing to a resting
            rotation, so a bag looks identical on every device despite
            different frame cadence.
          </li>
          <li>
            <strong className="text-foreground">Feedback:</strong> a
            world-anchored <Code>+3</Code> sprite flashes at the hole on any
            cornhole; sounds fire per phase (see below).
          </li>
        </ul>
      </Section>

      {/* ── Physics tuning + assets ────────────────────────────────── */}
      <Section icon={Sliders} title="Physics tuning & the asset pipeline" id="assets">
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniCard title="Global physics tuning">
            One DB row (<Code>physics_tuning</Code>, id <Code>default</Code>, a
            JSONB blob) holds every knob — gravity, friction, power ramp, arc
            height, lighting, bag size, lip capture. The{" "}
            <Code>/admin/physics</Code> tuner dock patches it; every device
            reads it on boot and the renderer calls <Code>getTuning()</Code>{" "}
            each frame, so changes are live. The <em>only</em> per-player knob
            is swipe power, kept in localStorage.
          </MiniCard>
          <MiniCard title="Assets: generate → R2 → catalog">
            <Code>assets</Code> rows come in four <Code>kind</Code>s —{" "}
            <Code>scene</Code>, <Code>board</Code>, <Code>bag</Code>,{" "}
            <Code>sfx</Code>. Scenes/boards/bags are generated from a text
            prompt via <strong>Gemini</strong> (image); SFX via{" "}
            <strong>ElevenLabs</strong> (audio). Bytes go to <strong>R2</strong>{" "}
            (<Code>imageKey</Code> + public <Code>imageUrl</Code>); a row carries{" "}
            per-kind <Code>metadata</Code> (palette, board dims, bag material,
            sfx gain), a <Code>status</Code>, and <Code>isBuiltIn</Code>.
            Managed in the unified <Code>/admin/design</Code> studio; built-ins
            are seeded on boot.
          </MiniCard>
        </div>
        <Prose>
          <strong>Slug is overloaded on purpose:</strong> for built-ins it's a
          stable id; for an <Code>sfx</Code> asset it's the{" "}
          <em>slot binding</em> (which game event the clip plays on). Multiple
          active sfx sharing a slug form a <strong>randomized pool</strong>.
        </Prose>
      </Section>

      {/* ── Sound ──────────────────────────────────────────────────── */}
      <Section icon={Volume2} title="Sound" id="sound">
        <Prose>
          Two layers. <Code>sfx-catalog.ts</Code> fetches active{" "}
          <Code>sfx</Code> assets, groups them by slot into pools, preloads one{" "}
          <Code>HTMLAudioElement</Code> per clip and clones on play (so
          overlapping fires don't cut out); each play picks a random clip from
          the slot's pool. If a slot has no clip (or audio fails), it falls back
          to a <strong>Web Audio synth</strong> in <Code>feedback.ts</Code>. Both
          are gated by the user's sound toggle. Slots:{" "}
          <Pill>round_start</Pill> <Pill>round_winner</Pill>{" "}
          <Pill>throw_release</Pill> <Pill>bag_land</Pill>{" "}
          <Pill>bag_in_hole</Pill> <Pill>bag_on_ground</Pill>{" "}
          <Pill>four_bagger</Pill> <Pill>game_win</Pill>.
        </Prose>
        <Prose>
          <strong>Live AI commentary</strong> (
          <Code>features/commentary</Code>, <Code>lib/commentary.ts</Code>) is a
          third, optional layer on the Scorer: each scoring event asks{" "}
          <Code>POST /api/commentary</Code> for a line — the server gathers game
          state + a stats block (PPR, win %, session series), prompts{" "}
          <strong>Gemini</strong> in the chosen persona (
          <Pill>smack</Pill> <Pill>funny</Pill> <Pill>savage</Pill>), runs a
          safety filter, and voices it with <strong>ElevenLabs TTS</strong>. The
          client gates by importance × a chattiness slider, plays lines
          one-at-a-time (freshest-wins) with an on-screen caption. Stateless;
          self-gates off without the AI keys + voice id.
        </Prose>
      </Section>

      {/* ── PWA & offline ──────────────────────────────────────────── */}
      <Section icon={Smartphone} title="PWA, offline & force-update" id="pwa">
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniCard title="Service worker strategy">
            <Code>apps/web/public/sw.js</Code>: network-first for navigations +{" "}
            <Code>/api/*</Code> (state must be authoritative), cache-first for
            hashed static assets, offline fallback page. Textures/generated
            assets are never SW-cached (HTTP <Code>max-age</Code> only).
          </MiniCard>
          <MiniCard title="Auto-versioned, force-updatable">
            A Vite plugin (<Code>inject-build-version</Code>) stamps a fresh{" "}
            <Code>CACHE_VERSION</Code> into <Code>sw.js</Code> every build, so
            the browser always detects a changed worker. <Code>UpdatePrompt</Code>{" "}
            surfaces a pill → tap posts <Code>skip-waiting</Code> →{" "}
            <Code>controllerchange</Code> reloads.{" "}
            <Code>version-check.ts</Code> also compares the running bundle hash
            against <Code>/api/version</Code> and hard-reloads stale clients
            (clearing caches + unregistering SWs).
          </MiniCard>
          <MiniCard title="Offline throw queue">
            <Code>throwQueue.ts</Code> persists un-POSTed solo throws in
            localStorage, survives reloads, and flushes on an interval + on{" "}
            <Code>online</Code>/<Code>focus</Code>. Idempotency keys make replay
            safe.
          </MiniCard>
          <MiniCard title="Web Push">
            VAPID web-push on game events (e.g. your turn / result).{" "}
            <Code>push_subscriptions</Code> stores endpoints; stale ones are
            pruned on 404/410 send failures.
          </MiniCard>
        </div>
      </Section>

      {/* ── Realtime & background jobs ─────────────────────────────── */}
      <Section icon={Radio} title="Realtime & background jobs" id="realtime">
        <div className="grid gap-3 sm:grid-cols-2">
          <MiniCard title="Realtime (SSE over LISTEN/NOTIFY)">
            <Code>games/stream.ts</Code> holds one dedicated Postgres{" "}
            <Code>LISTEN</Code> connection per process and fans notifications to
            local SSE subscribers via an in-process <Code>EventEmitter</Code>.
            Publishing emits locally <em>and</em> via <Code>pg_notify</Code>, so
            a viewer on any machine gets the event. Channel:{" "}
            <Code>game_&lt;shareCode&gt;</Code>.
          </MiniCard>
          <MiniCard title="Stats worker + 3-tier cache">
            Game completion enqueues a <Code>stats_jobs</Code> row. An
            in-process worker (started in bootstrap) claims jobs with{" "}
            <Code>FOR UPDATE SKIP LOCKED</Code>, recomputes all ranges
            (7d/30d/90d/all), and writes <Code>leaderboard_cache</Code>. Reads
            go LRU → DB cache → cold compute, so{" "}
            <Code>/api/leaderboards/by-range</Code> never 404s.
          </MiniCard>
        </div>
      </Section>

      {/* ── Auth & tenancy ─────────────────────────────────────────── */}
      <Section icon={ShieldCheck} title="Auth & tenancy" id="tenancy">
        <Prose>
          Sign-in is <strong>direct Google OAuth</strong> (GIS) → an
          HMAC-signed <Code>cornholio_session</Code> JWT cookie (HttpOnly,
          Secure, SameSite=Lax, 30-day sliding). Multi-provider ready via{" "}
          <Code>user_identities(provider, subject)</Code>. Middleware pipeline:{" "}
          <Code>requireAuth</Code> loads the user;{" "}
          <Code>tenancy</Code> resolves the operating org from the{" "}
          <Code>X-Org-Slug</Code> header, the role, and optional admin
          impersonation (<Code>X-Operating-As-User-Id</Code>);{" "}
          <Code>optionalAuth</Code> hydrates public routes. Services take{" "}
          <Code>(db, ctx, input)</Code> — never <Code>req</Code>.
        </Prose>
        <ul className="text-sm space-y-2 list-disc pl-5 text-muted-foreground">
          <li>
            <strong className="text-foreground">Orgs</strong> are the tenancy
            unit; <Code>parent_org_id</Code> enables white-label/reseller.
            Users belong to many via <Code>memberships(user_id, org_id, role)</Code>.
          </li>
          <li>
            <strong className="text-foreground">Player auto-created on org
            join.</strong> <strong className="text-foreground">Placeholder
            players</strong> (<Code>user_id NULL</Code> + email) are claimed on
            verified sign-in; a collision with an existing user creates a
            mutual-consent invite instead.
          </li>
          <li>
            <strong className="text-foreground">Platform admins</strong> via{" "}
            <Code>platform_admins</Code> gate <Code>/admin</Code>,{" "}
            <Code>/admin/design</Code>, <Code>/admin/physics</Code>, this page,
            and the test harness.
          </li>
        </ul>
      </Section>

      {/* ── Data model ─────────────────────────────────────────────── */}
      <Section icon={Database} title="Data model" id="schema">
        <p className="text-sm text-muted-foreground">
          22 tables. Drizzle is the source of truth — each table sole-sourced
          in a feature <Code>schema.ts</Code>; the barrel{" "}
          <Code>packages/db/src/schema.ts</Code> is a lint-enforced pure
          re-export.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TABLES.map((t) => (
            <Item key={t.label} label={t.label} value={t.value} />
          ))}
        </div>
      </Section>

      {/* ── API feature map ────────────────────────────────────────── */}
      <Section icon={Network} title="API feature map" id="api">
        <p className="text-sm text-muted-foreground mb-1">
          Feature modules under <Code>apps/api/src/features/</Code>, mounted at{" "}
          <Code>/api</Code> from <Code>routes/index.ts</Code>.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground font-display">
              <tr>
                <th className="px-3 py-2">Feature</th>
                <th className="px-3 py-2">Prefix</th>
                <th className="px-3 py-2">What it does</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {FEATURES.map((f) => (
                <tr key={f.name} className="align-top">
                  <td className="whitespace-nowrap px-3 py-2 font-medium">
                    {f.name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-primary">
                    {f.api}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── Conventions ────────────────────────────────────────────── */}
      <Section icon={Boxes} title="Conventions" id="conventions">
        <ul className="space-y-2 text-sm">
          {CONVENTIONS.map((c, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* ── Deploy & ops ───────────────────────────────────────────── */}
      <Section icon={Rocket} title="Deploy & ops" id="deploy">
        <Prose>
          Hosted on <strong>Fly.io</strong> (app <Code>cornholio</Code>, region{" "}
          <Code>ord</Code>, shared-cpu-1 / 512MB). The <Code>Dockerfile</Code>{" "}
          builds web then api, and the runtime image runs{" "}
          <Code>node dist/index.mjs</Code>; health at <Code>/api/healthz</Code>.
          DB is <strong>Neon</strong> project{" "}
          <Code>lively-sky-11419551</Code>; schema is applied with{" "}
          <strong>Drizzle migrations</strong> —{" "}
          <Code>pnpm db:generate</Code> then <Code>pnpm db:migrate</Code>{" "}
          (17 SQL files in <Code>packages/db/migrations</Code>). Domains:{" "}
          <Code>cornholio.app</Code> + <Code>cornholio.fly.dev</Code>. Generated
          assets serve from R2 at <Code>assets.cornholio.app</Code>.
        </Prose>
        <p className="text-sm text-muted-foreground mt-1">
          Deploy protocol: commit + <Code>git push</Code> every change (push
          does not deploy). Then check the thread for follow-ups; if none, run{" "}
          <Code>fly deploy</Code>. Schema changes on prod need explicit sign-off.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
          {ENV.map((e) => (
            <Item key={e.label} label={e.label} value={e.value} />
          ))}
        </div>
      </Section>

      {/* ── Tests ──────────────────────────────────────────────────── */}
      <Section icon={CheckCircle2} title="Test harness" id="tests">
        <Prose>
          In-process registry at <Code>/api/admin/tests</Code> (platform-admin,
          UI at <Code>/admin</Code>). Tests are tagged <Code>canary</Code>{" "}
          (gate deploys) vs informational, covering schema shape, tenancy
          invariants (no orphan memberships/players, every member has a player),
          scoring math, and code uniqueness. A dev-DB guard refuses to run
          against a production DSN unless explicitly overridden.
        </Prose>
      </Section>

      {/* ── Gotchas ────────────────────────────────────────────────── */}
      <Section icon={AlertTriangle} title="Gotchas that will bite you" id="gotchas">
        <div className="space-y-2">
          {GOTCHAS.map((g, i) => (
            <Callout key={i} title={g.t}>
              {g.d}
            </Callout>
          ))}
        </div>
      </Section>

      <footer className="pt-6 border-t border-border text-xs text-muted-foreground">
        Source:{" "}
        <a
          href="https://github.com/jvalenty/cornholio"
          className="text-primary hover:underline inline-flex items-center gap-1"
          target="_blank"
          rel="noreferrer"
        >
          jvalenty/cornholio <ExternalLink size={10} />
        </a>{" "}
        · Edit this doc at{" "}
        <Code>apps/web/src/pages/Architecture.tsx</Code>. Keep it in lockstep
        with the code — it's the handoff.
      </footer>
    </article>
  );
}

// ── 60-second mental model ──────────────────────────────────────────────
function Orientation() {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
      <h2 className="flex items-center gap-2 text-sm font-display uppercase tracking-widest text-primary">
        <Compass size={16} /> 60-second mental model
      </h2>
      <ol className="mt-3 space-y-2 text-sm text-foreground/90">
        <li>
          <strong>1.</strong> One Express process serves a tenant-aware JSON API{" "}
          <em>and</em> the built React SPA, plus the stats worker — cross-machine
          safe via Postgres <Code>LISTEN/NOTIFY</Code> + <Code>SKIP LOCKED</Code>.
        </li>
        <li>
          <strong>2.</strong> Gameplay is <strong>event-sourced</strong>. A
          throw is a command → <Code>game_events</Code> log +{" "}
          <Code>games</Code> projection. The same pure reducer{" "}
          (<Code>@cornholio/game-core</Code> · <Code>projectThrow</Code>) runs on
          the server AND the client, so solo play is offline-first and never
          drifts.
        </li>
        <li>
          <strong>3.</strong> The 3D game is a bespoke Three.js renderer
          (<Code>lib/virtual3d.ts</Code>) reading a single global physics-tuning
          row; opponent bags replay a deterministic scripted arc to a stored
          landing.
        </li>
        <li>
          <strong>4.</strong> Code is organized by <strong>feature directory</strong>{" "}
          (mirror on server + <Code>db</Code>). Everything is org-scoped; resolve
          it in middleware, never trust a client orgId.
        </li>
      </ol>
    </div>
  );
}

// ── Inline SVG diagrams (theme-safe: currentColor + brand accents) ───────
const BRAND = "hsl(355 70% 46%)"; // primary red
const GOLD = "hsl(40 90% 50%)"; // accent gold

function SystemDiagram() {
  return (
    <svg
      viewBox="0 0 760 320"
      className="w-full h-auto text-foreground"
      role="img"
      aria-label="System overview diagram"
    >
      <defs>
        <marker
          id="sys-arrow"
          viewBox="0 0 10 10"
          refX={9}
          refY={5}
          markerWidth={7}
          markerHeight={7}
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" opacity={0.5} />
        </marker>
      </defs>

      <SvgBox x={20} y={120} w={150} h={80} title="Browser / PWA" lines={["React SPA + SW", "Three.js renderer"]} accent={BRAND} />
      <SvgBox x={300} y={110} w={170} h={100} title="Fly VM" lines={["Express: /api + SPA", "stats worker (in-proc)"]} accent={GOLD} />

      <SvgBox x={600} y={20} w={140} h={54} title="Neon Postgres" lines={["events + projection"]} />
      <SvgBox x={600} y={92} w={140} h={54} title="Cloudflare R2" lines={["images · audio"]} />
      <SvgBox x={600} y={164} w={140} h={54} title="Gemini · ElevenLabs" lines={["asset generation"]} />
      <SvgBox x={600} y={236} w={140} h={54} title="Google OAuth · Push" lines={["auth · VAPID"]} />

      {/* client ↔ VM */}
      <SvgArrow x1={170} y1={150} x2={300} y2={150} label="HTTPS / SSE" both />
      {/* VM → deps */}
      <SvgArrow x1={470} y1={135} x2={600} y2={47} />
      <SvgArrow x1={470} y1={150} x2={600} y2={119} />
      <SvgArrow x1={470} y1={165} x2={600} y2={191} />
      <SvgArrow x1={470} y1={185} x2={600} y2={263} />

      <text x={95} y={235} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.6}>
        install · offline queue
      </text>
      <text x={385} y={235} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.6}>
        LISTEN/NOTIFY · SKIP LOCKED
      </text>
    </svg>
  );
}

function GameEngineDiagram() {
  return (
    <svg
      viewBox="0 0 760 300"
      className="w-full h-auto text-foreground"
      role="img"
      aria-label="Event-sourced game engine diagram"
    >
      <defs>
        <marker
          id="eng-arrow"
          viewBox="0 0 10 10"
          refX={9}
          refY={5}
          markerWidth={7}
          markerHeight={7}
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" opacity={0.5} />
        </marker>
      </defs>

      <SvgBox x={16} y={24} w={150} h={70} title="Client throw" lines={["outcome + landing", "clientEventUuid"]} accent={BRAND} />
      <SvgBox x={16} y={190} w={150} h={80} title="Solo path" lines={["projectThrow local", "apply + enqueue"]} />

      <SvgBox x={230} y={30} w={170} h={90} title="Server command" lines={["POST /games/:id/throws", "projectThrow (in tx)", "idempotent dedupe"]} accent={GOLD} />

      <SvgBox x={470} y={16} w={130} h={54} title="game_events" lines={["append-only log"]} />
      <SvgBox x={470} y={92} w={130} h={54} title="games" lines={["projection + version"]} />

      <SvgBox x={640} y={54} w={104} h={70} title="SSE" lines={["game_state", "+ version"]} accent={BRAND} />

      <SvgBox x={470} y={200} w={274} h={64} title="All viewers (any machine)" lines={["dedupe by version · replay via Last-Event-ID"]} />

      {/* flows */}
      <SvgArrow x1={166} y1={59} x2={230} y2={70} label="remote" />
      <SvgArrow x1={91} y1={94} x2={91} y2={190} label="offline-first" />
      <SvgArrow x1={166} y1={230} x2={230} y2={100} label="flush POST" dashed />
      <SvgArrow x1={400} y1={62} x2={470} y2={43} />
      <SvgArrow x1={400} y1={90} x2={470} y2={115} />
      <SvgArrow x1={600} y1={110} x2={640} y2={95} label="pg_notify" />
      <SvgArrow x1={690} y1={124} x2={620} y2={200} />
      <SvgArrow x1={470} y1={232} x2={166} y2={250} label="render" dashed both />
    </svg>
  );
}

function SvgBox({
  x,
  y,
  w,
  h,
  title,
  lines = [],
  accent,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  lines?: string[];
  accent?: string;
}) {
  const cx = x + w / 2;
  const titleY = lines.length ? y + h / 2 - lines.length * 6 + 2 : y + h / 2 + 4;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={8}
        fill={accent ?? "currentColor"}
        fillOpacity={accent ? 0.1 : 0.04}
        stroke={accent ?? "currentColor"}
        strokeOpacity={accent ? 0.7 : 0.25}
        strokeWidth={1.5}
      />
      <text x={cx} y={titleY} textAnchor="middle" fontSize={12.5} fontWeight={700} fill="currentColor">
        {title}
      </text>
      {lines.map((ln, i) => (
        <text
          key={i}
          x={cx}
          y={titleY + 15 + i * 13}
          textAnchor="middle"
          fontSize={10}
          fill="currentColor"
          opacity={0.65}
        >
          {ln}
        </text>
      ))}
    </g>
  );
}

function SvgArrow({
  x1,
  y1,
  x2,
  y2,
  label,
  dashed = false,
  both = false,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label?: string;
  dashed?: boolean;
  both?: boolean;
}) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const markerId =
    x2 > 600 || y2 > 180 || x1 < 200 ? "eng-arrow" : "sys-arrow";
  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="currentColor"
        strokeOpacity={0.4}
        strokeWidth={1.5}
        strokeDasharray={dashed ? "4 3" : undefined}
        markerEnd={`url(#${markerId})`}
        markerStart={both ? `url(#${markerId})` : undefined}
      />
      {label && (
        <text
          x={mx}
          y={my - 4}
          textAnchor="middle"
          fontSize={9}
          fill="currentColor"
          opacity={0.6}
        >
          {label}
        </text>
      )}
    </g>
  );
}

// ── small presentational helpers ────────────────────────────────────────
function Section({
  icon: Icon,
  title,
  id,
  children,
}: {
  icon: typeof Network;
  title: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="space-y-3 scroll-mt-6">
      <h2 className="flex items-center gap-2 border-b border-border pb-1.5 text-xl font-display uppercase tracking-tight">
        <Icon size={18} className="text-primary" /> {title}
      </h2>
      {children}
    </section>
  );
}

function Figure({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 sm:p-4">
      {children}
    </div>
  );
}

function Prose({ children }: { children: ReactNode }) {
  return <p className="max-w-3xl text-sm leading-relaxed">{children}</p>;
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.8em]">
      {children}
    </code>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="mr-1 inline-block rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-xs">
      {children}
    </span>
  );
}

function MiniCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-sm font-semibold">{title}</div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {children}
        </p>
      </CardContent>
    </Card>
  );
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-accent/40 bg-accent/10 p-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold">
        <AlertTriangle size={14} className="text-accent" /> {title}
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {children}
      </p>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <div className="text-sm mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

// ── content data ─────────────────────────────────────────────────────────
const STACK = [
  { k: "Repo", v: "pnpm monorepo · TypeScript" },
  { k: "Server", v: "Node 22 · Express · esbuild" },
  { k: "Client", v: "React 18 · Vite 5 · wouter" },
  { k: "Data layer", v: "TanStack Query v5" },
  { k: "Styling", v: "Tailwind v4 · Radix" },
  { k: "3D", v: "Three.js (lazy chunk)" },
  { k: "ORM", v: "Drizzle · node-postgres" },
  { k: "Database", v: "Neon Postgres" },
  { k: "Storage", v: "Cloudflare R2 (S3 API)" },
  { k: "AI", v: "Gemini (image + commentary text) · ElevenLabs (sfx + TTS voice)" },
  { k: "Auth", v: "Google OAuth · JWT cookie" },
  { k: "Host", v: "Fly.io · region ord" },
];

const TABLES: { label: string; value: string }[] = [
  { label: "games", value: "Event-sourced projection: format/status enums, scoringMethod (net | both_sides), scores, currentRound, currentTurn, throwsThisRound jsonb, bagsRemainingT1/2, version, winningTeam, isVirtual + virtualScene/board/bag, shareCode." },
  { label: "game_events", value: "Append-only log. event_seq (monotonic, gap-free), eventType (game_created/throw_landed/round_committed/game_completed/game_voided), payload jsonb, clientEventUuid (idempotency), actorUserId." },
  { label: "game_players / game_frames", value: "Roster per game (team/seat, ceremonyShownAt); per-round scoring — netPoints + scoringTeam (net mode), team1_gross/team2_gross (both-sides mode → net derived), in-hole/on-board tallies." },
  { label: "assets", value: "kind scene/board/bag/sfx · prompt · imageUrl + imageKey (R2) · metadata jsonb (per-kind) · status · isBuiltIn · slug (built-in id / sfx slot binding)." },
  { label: "physics_tuning", value: "Single row id='default' · data jsonb — the whole global tuning blob synced to every device." },
  { label: "orgs", value: "slug, parent_org_id (hierarchy), brandConfig jsonb, timezone." },
  { label: "users / user_identities", value: "Global users (@-slug); identities fan out auth by (provider, subject) — google/apple/microsoft." },
  { label: "memberships / platform_admins", value: "User↔org role (owner/admin/member); platform_admins grants global admin." },
  { label: "players / connections", value: "Org-scoped competitor (user_id nullable = placeholder, email for claim); P2P user connections (canonical sorted pair)." },
  { label: "teams / invites", value: "Doubles rosters; 12-char invite codes (org or P2P), lazy toUserId, status lifecycle." },
  { label: "events / event_registrations", value: "Events (isPublic + lat/long for discovery); registrations for a player OR team, with seed + check-in." },
  { label: "tournaments / bracket_matches", value: "single_elim/double_elim/round_robin/pool_to_bracket; match slots with source enum (seed/winner_of/loser_of), gameId, boardNumber." },
  { label: "push_subscriptions", value: "Web Push endpoints (unique) + p256dh/auth keys." },
  { label: "stats_jobs / leaderboard_cache", value: "Refresh queue (claimed SKIP LOCKED) + materialized leaderboard per (orgId, range)." },
];

const FEATURES: { name: string; api: string; desc: string }[] = [
  { name: "auth", api: "/api/auth · /api/me", desc: "Google sign-in, session cookie, current user." },
  { name: "orgs", api: "/api/orgs", desc: "Org CRUD, hierarchy, current-org, public profile." },
  { name: "users", api: "/api/me", desc: "Self-profile update (display name, @-slug)." },
  { name: "platform-admin", api: "/api/admin/users", desc: "List users, grant/revoke platform admin." },
  { name: "players", api: "/api/players", desc: "Roster CRUD + CPU/Ghost factories; /:id/scoring-stats = per-player offense/defense (PPR) from both-sides games." },
  { name: "connections / invites", api: "/api/invites", desc: "Org + P2P invites: create, accept, decline, list." },
  { name: "teams", api: "/api/teams", desc: "Doubles team CRUD + public team page." },
  { name: "events", api: "/api/events", desc: "Event CRUD + registrations + public event pages." },
  { name: "events-public", api: "/api/events-public", desc: "Discovery: search / filter by location, date, format." },
  { name: "tournaments", api: "/api/tournaments", desc: "Bracket lifecycle, seeding, board assignment." },
  { name: "games", api: "/api/games · /api/g/:code", desc: "Game CRUD, frames (net + both-sides gross), event-sourced throws, SSE stream + play-state; /:id/championship = best-of-3 rematch-chain clinch (inferred, 6h window)." },
  { name: "leaderboards", api: "/api/leaderboards", desc: "Master / teams / head-to-head with game minimums." },
  { name: "stats", api: "/api/leaderboards/by-range · /api/stats/scoring", desc: "Time-windowed leaderboards (3-tier cache) + org-wide both-sides scoring analytics (offense/defense leaders, cancellation rate)." },
  { name: "commentary", api: "/api/commentary", desc: "Live AI announcer: game state + stats block → Gemini line (persona: smack/funny/savage) → ElevenLabs TTS → {text, audio}. Self-gates on GEMINI/ELEVENLABS keys + voice id." },
  { name: "assets", api: "/api/assets · /api/admin/assets", desc: "Public catalog + admin generate (Gemini image / ElevenLabs sfx) + upload." },
  { name: "physics-tuning", api: "/api/physics-tuning", desc: "Read the global tuning blob; platform-admin patch." },
  { name: "push", api: "/api/push", desc: "VAPID key, subscribe/unsubscribe, test push." },
  { name: "og / org-public", api: "/api/og · /api/orgs/:slug/public", desc: "Social share PNGs (resvg) + public org snapshots." },
  { name: "admin-tests", api: "/api/admin/tests", desc: "Canary + informational test harness gating deploys." },
];

const CONVENTIONS: ReactNode[] = [
  <>
    <strong>Feature directories, no monoliths.</strong> Server, web, and db all
    organize by feature; split any file over ~800 lines.
  </>,
  <>
    <strong>Services take (db, ctx, input), never req.</strong> Routes are thin
    Express handlers wrapped in <Code>asyncHandler</Code>; errors flow through{" "}
    <Code>errorHandler</Code> (HttpError + ZodError).
  </>,
  <>
    <strong>Game rules live once</strong> in <Code>@cornholio/game-core</Code>.
    If you change scoring, change it there — both server and client consume it.
  </>,
  <>
    <strong>Schema = Drizzle, applied by migrations.</strong> Edit a feature{" "}
    <Code>schema.ts</Code> → <Code>pnpm db:generate</Code> →{" "}
    <Code>pnpm db:migrate</Code>. The barrel is a pure re-export.
  </>,
  <>
    <strong>Detail pages, not modals</strong> for full-record editing; list →{" "}
    <Code>/&lt;feature&gt;/:id</Code>.
  </>,
  <>
    <strong>Tenancy first.</strong> Start from the resolved operating context;
    never trust a client-supplied orgId beyond what middleware authorized.
  </>,
];

const ENV: { label: string; value: string }[] = [
  { label: "Database", value: "DATABASE_URL — Neon lively-sky-11419551 (use the direct, non-pooler endpoint)." },
  { label: "Auth", value: "GOOGLE_CLIENT_ID · SESSION_SECRET · PUBLIC_APP_URL. Build-time VITE_GOOGLE_CLIENT_ID baked from committed .env.production." },
  { label: "Storage (R2)", value: "R2_ACCOUNT_ID · R2_BUCKET · R2_ACCESS_KEY_ID · R2_SECRET_ACCESS_KEY · R2_PUBLIC_BASE_URL (assets.cornholio.app)." },
  { label: "AI + push", value: "GEMINI_API_KEY (+ GEMINI_MODEL image, GEMINI_TEXT_MODEL commentary) · ELEVENLABS_API_KEY (+ ELEVENLABS_VOICE_ID + ELEVENLABS_TTS_MODEL for commentary voice) · VAPID_PUBLIC_KEY · VAPID_PRIVATE_KEY · VAPID_CONTACT." },
];

const GOTCHAS: { t: string; d: string }[] = [
  {
    t: "R2 no-CORS cache poisoning on textures",
    d: "A texture first fetched without CORS gets cached opaque; Three.js then can't read it (blank board/scene). Fixes in place: R2 CORS policy + cache-busting URL params (withBust) + loadImageTexture for the scene aligner. If a generated asset renders blank on one device but not another, suspect a poisoned cache entry, not the asset.",
  },
  {
    t: "Migrations, not db:push",
    d: "Unlike the killer-core sibling, cornholio applies schema via generated Drizzle migrations (pnpm db:generate → db:migrate), 17 files in packages/db/migrations. Do not hand-edit applied migrations or switch to db:push. Prod schema changes need explicit sign-off; the migrate runner can also apply via Neon MCP when DNS is flaky.",
  },
  {
    t: "In-flight sends & the round-reset race",
    d: "A deploy restarts the VM; that's fine for turn-based games but an opponent bag mid-flight when a round commits used to get wiped by resetBoard. The remote-animation queue now DEFERS the board clear until animations drain (pendingBoardResetRef). If you touch round transitions, preserve that.",
  },
  {
    t: "Scripted in_hole target is the hole SURFACE, not the stored bottom",
    d: "A player's physics 3-pointer stores its landing at the hole BOTTOM (y≈0). Replaying that arc verbatim flew the bag under the deck (it 'vanished'). throwBagScripted normalizes in_hole target.y to the slab surface, then scriptedStep drops it into the throat. Keep that normalization if you refactor the scripted flight.",
  },
  {
    t: "Fly cold starts + secret-injection lag",
    d: "min_machines_running=0 means the first request after idle cold-starts. And a secret added AFTER a machine exists isn't injected by a normal redeploy — destroy/recreate the machine (non-secret flags belong in fly.toml [env]).",
  },
  {
    t: "Auto-versioned SW means every deploy forces an update",
    d: "inject-build-version stamps a fresh CACHE_VERSION each build, so clients reload on the next visit (or via the UpdatePrompt pill). Good for shipping fixes fast — but don't rely on a client running old code; version-check.ts will hard-reload stale bundles.",
  },
];
